import React from 'react';
import { Box, FilterOptionsState } from '@mui/material';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SingleSelect } from '../single-select';
import { MultiSelect } from './multi-select';
import { ReadonlyInput } from '../readonly-input';
import { Row } from '../row';
import { Cell } from '../cell';
import { Tooltip } from '../tooltip';
import { ColorPalette } from '../config/color-palette';

const UOPLYST_MAALGRUPPE_ID = '152a3d6b-8be9-4dae-8ba5-e796a51369e9';

export const ADDITIONAL_REQUIRED_PRIMARY_IDS = [
  'f1c7ee41-21b4-4dba-9424-c874bf72b5e7', // "Multipel funktionsnedsættelse"
  '1f23325e-c9c9-455b-bfe7-b4dcd1bb63e5', // "Sjældent forekommende funktionsnedsættelse"
];

export interface IPrimaryAndAdditionalTargetGroupSelectorProps<T extends { id?: string }> {
  primaryLabel?: string;
  additionalLabel?: string;
  primaryValue?: T;
  additionalValues: T[];
  options: T[];
  onPrimaryChange: (value: T | undefined) => void;
  onAdditionalChange: (values: T[]) => void;
  getOptionLabel: (option: T) => string;
  renderOption: (prop: object, option: T) => React.ReactNode;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  getOptionDisabled?: (option: T) => boolean;
  additionalRequiredWhenPrimaryIds?: string[];
  isReadOnly?: boolean;
  disabled?: boolean;
  primaryRequired?: boolean;
  primaryError?: boolean;
  primaryErrorText?: string;
  additionalError?: boolean;
  additionalErrorText?: string;
  variant?: 'standard' | 'filled' | 'outlined';
}

export function PrimaryAndAdditionalTargetGroupSelector<T extends { id?: string }>(
  props: IPrimaryAndAdditionalTargetGroupSelectorProps<T>
) {
  const {
    primaryLabel = 'Primær målgruppe',
    additionalLabel = 'Øvrige målgrupper',
    primaryValue,
    additionalValues,
    options,
    onPrimaryChange,
    onAdditionalChange,
    getOptionLabel,
    renderOption,
    filterOptions,
    getOptionDisabled,
    additionalRequiredWhenPrimaryIds,
    isReadOnly,
    disabled,
    primaryRequired,
    primaryError,
    primaryErrorText,
    additionalError,
    additionalErrorText,
    variant,
  } = props;

  const isAdditionalRequired = (additionalRequiredWhenPrimaryIds ?? []).includes(primaryValue?.id ?? '');

  const [additionalTouched, setAdditionalTouched] = React.useState(false);
  const prevIsAdditionalRequired = React.useRef(isAdditionalRequired);
  React.useEffect(() => {
    if (isAdditionalRequired && !prevIsAdditionalRequired.current) {
      setAdditionalTouched(false);
    }
    prevIsAdditionalRequired.current = isAdditionalRequired;
  }, [isAdditionalRequired]);

  const showRequiredError = isAdditionalRequired && additionalTouched && additionalValues.length === 0;

  const additionalOptions = options.filter(x => x.id !== UOPLYST_MAALGRUPPE_ID);

  function isPrimaryOptionDisabled(option: T): boolean {
    return (getOptionDisabled?.(option) ?? false) || additionalValues.some(v => v.id === option.id);
  }

  function isAdditionalOptionDisabled(option: T): boolean {
    return (getOptionDisabled?.(option) ?? false) || option.id === primaryValue?.id;
  }

  function handleAdditionalChange(values: T[]) {
    if (isAdditionalRequired) setAdditionalTouched(true);
    onAdditionalChange(values);
  }

  // The call sites set opacity:1 on every option to keep category headers visible,
  // which overrides MUI's built-in disabled styling. We cloneElement the rendered
  // node and inject a background colour + suffix label only for options muted by
  // mutual exclusion (not for structural non-selectable headers).
  function withMutedBackground(isExcluded: (option: T) => boolean, suffixLabel: string) {
    return (prop: object, option: T): React.ReactNode => {
      const element = renderOption(prop, option) as React.ReactElement;
      if (!isExcluded(option)) return element;
      return React.cloneElement(
        element,
        { style: { ...(element.props.style ?? {}), backgroundColor: '#f0f0f0' } },
        ...React.Children.toArray(element.props.children),
        <span style={{ marginLeft: 8, color: '#9e9e9e', fontSize: '0.85em' }}>{suffixLabel}</span>
      );
    };
  }

  const primaryRenderOption = withMutedBackground(
    option => additionalValues.some(v => v.id === option.id),
    '(Valgt i øvrige)'
  );
  const additionalRenderOption = withMutedBackground(
    option => option.id === primaryValue?.id,
    '(Valgt i primær)'
  );

  const additionalLabelWithIcon = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {additionalLabel}
      <Tooltip title="Øvrige målgrupper viser kun målgrupper der ikke er valgt som primær og viser aldrig målgruppen Uoplyst målgruppe">
        <Box component="span" sx={{ color: ColorPalette.HyperLink, display: 'inline-flex' }}>
          <FontAwesomeIcon icon={faCircleInfo} size="lg" />
        </Box>
      </Tooltip>
    </Box>
  );

  if (isReadOnly) {
    return (
      <>
        <Row>
          <Cell size="full">
            <ReadonlyInput label={primaryLabel} value={primaryValue ? getOptionLabel(primaryValue) : undefined} />
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <ReadonlyInput label={additionalLabel} value={additionalValues.map(v => getOptionLabel(v)).join(', ')} />
          </Cell>
        </Row>
      </>
    );
  }

  return (
    <>
      <Row>
        <Cell size="full">
          <SingleSelect<T>
            label={primaryLabel}
            getOptionLabel={getOptionLabel}
            value={primaryValue}
            options={options}
            renderOption={(prop, option) => primaryRenderOption(prop, option) as React.ReactElement}
            filterOptions={filterOptions}
            getOptionDisabled={isPrimaryOptionDisabled}
            onChange={value => onPrimaryChange(value ?? undefined)}
            variant={variant}
            disabled={disabled}
            required={primaryRequired}
            error={primaryError}
            errorText={primaryErrorText}
          />
        </Cell>
      </Row>
      <Row>
        <Cell size="full">
          <MultiSelect<T>
            label={additionalLabelWithIcon}
            getOptionLabel={getOptionLabel}
            value={additionalValues}
            options={additionalOptions}
            renderOption={additionalRenderOption}
            filterOptions={filterOptions}
            getOptionDisabled={isAdditionalOptionDisabled}
            onChange={handleAdditionalChange}
            variant={variant}
            disabled={disabled}
            required={isAdditionalRequired}
            error={showRequiredError || !!additionalError}
            errorText={showRequiredError ? 'Mindst én øvrig målgruppe skal vælges for den valgte primære målgruppe' : additionalErrorText}
          />
        </Cell>
      </Row>
    </>
  );
}
